import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";

export const Route = createFileRoute("/admin/announcements")({
  component: AdminAnnouncements,
});

type Announcement = { id: string; message: string; position: number; enabled: boolean };

function AdminAnnouncements() {
  const qc = useQueryClient();
  const [newMessage, setNewMessage] = useState("");

  const { data: items = [] } = useQuery({
    queryKey: ["admin", "announcements"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .order("position", { ascending: true });
      if (error) throw error;
      return data as Announcement[];
    },
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "announcements"] });

  const add = async () => {
    if (!newMessage.trim()) return;
    const pos = items.length;
    const { error } = await supabase.from("announcements").insert({ message: newMessage.trim(), position: pos, enabled: true });
    if (error) return toast.error(error.message);
    setNewMessage("");
    toast.success("Added");
    refresh();
  };

  const update = async (id: string, patch: Partial<Announcement>) => {
    const { error } = await supabase.from("announcements").update(patch).eq("id", id);
    if (error) return toast.error(error.message);
    refresh();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("announcements").delete().eq("id", id);
    if (error) return toast.error(error.message);
    toast.success("Removed");
    refresh();
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const a = items[idx], b = items[j];
    await supabase.from("announcements").update({ position: b.position }).eq("id", a.id);
    await supabase.from("announcements").update({ position: a.position }).eq("id", b.id);
    refresh();
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-medium mb-1">Announcement Bar</h2>
        <p className="text-sm text-muted-foreground">Running messages shown above the categories on every page.</p>
      </div>

      <div className="flex gap-2">
        <input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="New announcement message"
          className="flex-1 border border-border bg-background px-3 py-2 text-sm"
        />
        <button onClick={add} className="inline-flex items-center gap-2 bg-primary px-4 py-2 text-sm text-primary-foreground">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <div className="border border-border divide-y divide-border">
        {items.length === 0 && <p className="p-4 text-sm text-muted-foreground">No announcements yet.</p>}
        {items.map((a, i) => (
          <Row key={a.id} a={a} index={i} count={items.length} onUpdate={update} onRemove={remove} onMove={move} />
        ))}
      </div>
    </div>
  );
}

function Row({ a, index, count, onUpdate, onRemove, onMove }: {
  a: Announcement; index: number; count: number;
  onUpdate: (id: string, patch: Partial<Announcement>) => void;
  onRemove: (id: string) => void;
  onMove: (idx: number, dir: -1 | 1) => void;
}) {
  const [msg, setMsg] = useState(a.message);
  return (
    <div className="flex items-center gap-2 p-3">
      <input
        value={msg}
        onChange={(e) => setMsg(e.target.value)}
        className="flex-1 border border-border bg-background px-3 py-2 text-sm"
      />
      <label className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        <input type="checkbox" checked={a.enabled} onChange={(e) => onUpdate(a.id, { enabled: e.target.checked })} />
        On
      </label>
      <button onClick={() => onUpdate(a.id, { message: msg })} className="p-2 border border-border" title="Save"><Save className="h-4 w-4" /></button>
      <button onClick={() => onMove(index, -1)} disabled={index === 0} className="p-2 border border-border disabled:opacity-40"><ArrowUp className="h-4 w-4" /></button>
      <button onClick={() => onMove(index, 1)} disabled={index === count - 1} className="p-2 border border-border disabled:opacity-40"><ArrowDown className="h-4 w-4" /></button>
      <button onClick={() => onRemove(a.id)} className="p-2 border border-border text-destructive"><Trash2 className="h-4 w-4" /></button>
    </div>
  );
}
